import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useAppDispatch } from 'src/store';
import {
  useCreateFeedbackMutation,
  useDeleteFeedbackMutation,
  useUpdateFeedbackMutation,
} from 'src/store/services';
import {
  replaceConversationMessageFields,
  overwriteConversationMessage,
} from 'src/store/updateQueries';
import { COMMON_ERROR_TEXT } from 'src/constants';
import {
  CreateFeedbackRequest,
  Feedback,
  FeedbackOperationType,
  FeedbackType,
  Message,
  PatchFeedbackRequest,
  ModelFeedback,
  MessageTopLevelFeedback,
  FeedbackV2Type,
} from 'src/types';
import { getErrorStatus } from 'src/utils';
import { useGetUser } from '../userHooks';
import { useMessageListRefActions } from '../conversationHooks';

interface FeedbackArgs {
  rating: number;
  operationType: FeedbackOperationType;
  modelId?: string;
}

interface ExtendedFeedbackArgs extends FeedbackArgs {
  details?: string;
}

const SUBMITTED_TEXT = 'Thank you for the feedback';
const CANCELLED_TEXT = 'Feedback cancelled';

interface UseFeedbackRatingArgs {
  message: Message;
  modelId?: string;
  operationType: FeedbackOperationType;
}

export const useFeedbackRating = ({
  message,
  modelId,
  operationType,
}: UseFeedbackRatingArgs) => {
  const dispatch = useAppDispatch();

  const { user_id } = useGetUser();
  const { getCurrentMessageListRef } = useMessageListRefActions();

  const { conversation_id, feedback, message_id } = message;

  const currentRating = useMemo(() => {
    if (!feedback?.feedbacks || feedback?.feedbacks.length === 0) {
      return;
    }

    if (operationType === 'models') {
      return (
        feedback.feedbacks.find(
          (item) => (item.data as ModelFeedback)?.model_id === modelId,
        )?.data as ModelFeedback
      )?.rating.rating;
    }

    if (operationType === 'message-level') {
      return (
        feedback.feedbacks.find(
          (item) =>
            item.data.feedback_type === FeedbackV2Type.MESSAGE_TOP_LEVEL,
        )?.data as MessageTopLevelFeedback
      )?.rating?.rating;
    }

    return;
  }, [operationType, modelId, feedback]);

  const currentFeedbackId = useMemo(() => {
    if (operationType === 'message-level') {
      return message?.feedback?.feedbacks?.find(
        (item) => item.data.feedback_type === FeedbackV2Type.MESSAGE_TOP_LEVEL,
      )?.feedback_id;
    }

    if (operationType === 'models') {
      return message?.feedback?.feedbacks?.find(
        (item) => (item.data as ModelFeedback).model_id === modelId,
      )?.feedback_id;
    }
    return;
  }, [operationType, message, modelId]);

  const [createFeedback, { isLoading: isCreateLoading }] =
    useCreateFeedbackMutation();
  const [deleteFeedback, { isLoading: isDeleteLoading }] =
    useDeleteFeedbackMutation();
  const [updateFeedback, { isLoading: isUpdateLoading }] =
    useUpdateFeedbackMutation();

  const isLoading = useMemo(
    () => isCreateLoading || isDeleteLoading || isUpdateLoading,
    [isCreateLoading, isDeleteLoading, isUpdateLoading],
  );

  const updateMessageWithFeedback = useCallback(
    ({
      message,
      feedback_id,
      rating,
      feedback_ids,
      updated_feedback,
    }: {
      message: Message;
      feedback_id?: string;
      rating: number | null;
      feedback_ids?: Array<string>;
      updated_feedback?: Feedback | null;
    }): Message => {
      if (updated_feedback && feedback_ids) {
        const updatedMessage = {
          ...message,
          feedback: updated_feedback,
          feedback_ids,
        };

        return updatedMessage;
      }

      // optimistic update handling

      if (rating === null) {
        const updatedMessage: Message = {
          ...message,
          feedback: {
            feedbacks: feedback?.feedbacks?.filter(
              (item) => item.feedback_id !== feedback_id,
            ),
          },
        };

        return updatedMessage;
      }

      const messageLevelFeedback =
        operationType === 'message-level'
          ? [
              {
                feedback_id: feedback_id || '',
                data: {
                  feedback_type: FeedbackV2Type.MESSAGE_TOP_LEVEL,
                  rating: {
                    rating,
                  },
                },
              },
            ]
          : [];

      const modelsFeedback =
        operationType === 'models' && !!modelId
          ? [
              {
                feedback_id: feedback_id || '',
                data: {
                  feedback_type: FeedbackV2Type.MODEL_FEEDBACK,
                  model_id: modelId || '',
                  rating: {
                    rating,
                  },
                },
              },
            ]
          : [];

      const updatedMessage: Message = {
        ...message,
        feedback: {
          feedbacks: [
            ...(feedback?.feedbacks || []),
            ...messageLevelFeedback,
            ...modelsFeedback,
          ],
        },
      };

      return updatedMessage;
    },
    [modelId, operationType, feedback],
  );

  const getFeedbackData = useCallback(
    ({
      operationType,
      rating,
      modelId,
    }: FeedbackArgs): ModelFeedback | MessageTopLevelFeedback => {
      if (operationType === 'message-level') {
        return {
          feedback_type: FeedbackV2Type.MESSAGE_TOP_LEVEL,
          rating: {
            rating,
          },
        };
      }

      return {
        feedback_type: FeedbackV2Type.MODEL_FEEDBACK,
        model_id: modelId || '',
        rating: {
          rating,
        },
      };
    },
    [],
  );

  const handleDeleteFeedback = useCallback(
    async (messageId: string) => {
      if (!currentFeedbackId || !message || !conversation_id) {
        return;
      }

      const messageListRef = getCurrentMessageListRef(conversation_id);

      const temporaryReplacedMessage = updateMessageWithFeedback({
        message,
        feedback_id: currentFeedbackId,
        rating: null,
      });

      dispatch(
        replaceConversationMessageFields({
          message: temporaryReplacedMessage,
          messageListRef,
        }),
      );

      try {
        const { feedback_ids, updated_feedback } = await deleteFeedback({
          conversation_id,
          message_id: messageId,
          feedback_id: currentFeedbackId,
          user_id,
        }).unwrap();
        toast(CANCELLED_TEXT);

        const updatedMessage = updateMessageWithFeedback({
          message,
          feedback_id: currentFeedbackId,
          rating: null,
          feedback_ids,
          updated_feedback,
        });
        dispatch(
          overwriteConversationMessage({
            message: updatedMessage,
            messageListRef,
          }),
        );
      } catch (error) {
        toast.error(COMMON_ERROR_TEXT);
        dispatch(
          overwriteConversationMessage({
            message,
            messageListRef,
          }),
        );
      }
    },
    [
      currentFeedbackId,
      message,
      conversation_id,
      getCurrentMessageListRef,
      updateMessageWithFeedback,
      dispatch,
      deleteFeedback,
      user_id,
    ],
  );

  const handleCreateFeedback = useCallback(
    async ({ rating, operationType, modelId }: FeedbackArgs) => {
      if (!message || !message_id || !conversation_id) {
        return;
      }

      const messageListRef = getCurrentMessageListRef(conversation_id);

      const body: CreateFeedbackRequest = {
        user_id,
        conversation_id,
        message_id,
        feedback_type: FeedbackType.FEEDBACK_V2,
        feedback_details_v2: getFeedbackData({
          rating,
          operationType,
          modelId,
        }),
      };

      const temporaryReplacedMessage: Message = updateMessageWithFeedback({
        message: message,
        rating,
      });

      dispatch(
        replaceConversationMessageFields({
          message: temporaryReplacedMessage,
          messageListRef,
        }),
      );

      try {
        const { updated_feedback, feedback_ids } = await createFeedback(
          body,
        ).unwrap();
        toast.success(SUBMITTED_TEXT);

        const updatedMessage = updateMessageWithFeedback({
          message: message,
          feedback_ids,
          updated_feedback,
          rating,
        });

        dispatch(
          overwriteConversationMessage({
            message: updatedMessage,
            messageListRef,
          }),
        );
      } catch (error) {
        if (getErrorStatus(error) === 404) {
          toast.error(
            'Error saving feedback. Please wait until the response is fully streamed.',
          );
        } else {
          toast.error(COMMON_ERROR_TEXT);
        }

        dispatch(
          overwriteConversationMessage({
            message,
            messageListRef,
          }),
        );
      }
    },
    [
      message,
      message_id,
      conversation_id,
      getCurrentMessageListRef,
      user_id,
      getFeedbackData,
      updateMessageWithFeedback,
      dispatch,
      createFeedback,
    ],
  );

  const handleUpdateFeedback = useCallback(
    async ({
      messageId,
      feedbackId,
      rating,
      details,
    }: {
      messageId: string;
      rating: number;
      feedbackId?: string;
      details?: string;
    }) => {
      if (!message || !feedbackId || !conversation_id) {
        return;
      }

      const messageListRef = getCurrentMessageListRef(conversation_id);

      const body: PatchFeedbackRequest = {
        user_id,
        conversation_id,
        message_id: messageId,
        feedback_id: feedbackId,
        feedback_details_v2:
          operationType === 'message-level'
            ? {
                feedback_type: FeedbackV2Type.MESSAGE_TOP_LEVEL,
                rating: {
                  rating,
                  reason: details,
                },
              }
            : {
                feedback_type: FeedbackV2Type.MODEL_FEEDBACK,
                model_id: modelId || '',
                rating: {
                  rating,
                  reason: details,
                },
              },
      };

      const temporaryReplacedMessage: Message = updateMessageWithFeedback({
        message: message,
        rating,
      });

      dispatch(
        replaceConversationMessageFields({
          message: temporaryReplacedMessage,
          messageListRef,
        }),
      );

      try {
        const { feedback_ids, updated_feedback } = await updateFeedback(
          body,
        ).unwrap();
        toast.success(SUBMITTED_TEXT);

        const updatedMessage: Message = updateMessageWithFeedback({
          message: message,
          rating,
          feedback_ids,
          updated_feedback,
        });

        dispatch(
          overwriteConversationMessage({
            message: updatedMessage,
            messageListRef,
          }),
        );
      } catch (error) {
        toast.error(COMMON_ERROR_TEXT);
        dispatch(
          overwriteConversationMessage({
            message,
            messageListRef,
          }),
        );
      }
    },
    [
      message,
      conversation_id,
      getCurrentMessageListRef,
      user_id,
      operationType,
      modelId,
      updateMessageWithFeedback,
      dispatch,
      updateFeedback,
    ],
  );

  const updateFeedbackRating = useCallback(
    async ({
      rating,
      operationType,
      modelId,
      details,
    }: ExtendedFeedbackArgs) => {
      if (!message_id || isLoading) {
        return;
      }

      if (currentRating === rating) {
        await handleDeleteFeedback(message_id);
        return;
      }

      if (currentFeedbackId) {
        await handleUpdateFeedback({
          messageId: message_id,
          rating,
          feedbackId: currentFeedbackId,
          details,
        });
        return;
      }

      await handleCreateFeedback({
        rating,
        operationType,
        modelId,
      });
    },
    [
      currentFeedbackId,
      message_id,
      currentRating,
      isLoading,
      handleDeleteFeedback,
      handleUpdateFeedback,
      handleCreateFeedback,
    ],
  );

  const addFeedbackDetails = useCallback(
    async (details: string) => {
      if (!message_id || !currentFeedbackId || !currentRating) {
        return;
      }

      await handleUpdateFeedback({
        messageId: message_id,
        rating: currentRating,
        feedbackId: currentFeedbackId,
        details,
      });
      return;
    },
    [handleUpdateFeedback, message_id, currentFeedbackId, currentRating],
  );

  return useMemo(
    () => ({
      currentRating,
      updateFeedbackRating,
      addFeedbackDetails,
      isLoading,
    }),
    [currentRating, updateFeedbackRating, addFeedbackDetails, isLoading],
  );
};
